import { Component, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { environment } from '../../../environments/environment';

declare const google: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="login-page">

      <!-- Left Brand Panel -->
      <div class="brand-panel">
        <div class="brand-panel-inner">
          <!-- Decorative lines -->
          <div class="deco-line deco-line-1"></div>
          <div class="deco-line deco-line-2"></div>
          <div class="deco-circle"></div>

          <div class="brand-content">
            <div class="brand-logo-mark">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #0B0F17;">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h1 class="brand-title">Shell Muze</h1>
            <p class="brand-tagline">Premium Custom Phone Case Studio</p>

            <div class="brand-divider"></div>

            <div class="brand-feature-list">
              <div class="brand-feature">
                <div class="feature-dot"></div>
                <span>Manage luxury product catalog</span>
              </div>
              <div class="brand-feature">
                <div class="feature-dot"></div>
                <span>Track & respond to enquiries</span>
              </div>
              <div class="brand-feature">
                <div class="feature-dot"></div>
                <span>Control banners & collections</span>
              </div>
            </div>
          </div>

          <div class="brand-panel-footer">
            <span>© 2025 Shell Muze · All rights reserved</span>
          </div>
        </div>
      </div>

      <!-- Right Form Panel -->
      <div class="form-panel">
        <div class="form-card animate-in">

          <div class="form-header">
            <div class="form-logo">
              <div class="form-logo-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="color: #0B0F17;">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
            </div>
            <h2 class="form-title">Welcome back</h2>
            <p class="form-subtitle">Sign in to the Shell Muze admin portal</p>
          </div>

          <div *ngIf="errorMessage" class="error-alert">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {{ errorMessage }}
          </div>

          <!-- Google Sign-in Section -->
          <div class="google-auth-container">
            <div id="admin-google-btn" class="google-render-btn"></div>
            <button type="button" class="custom-google-btn" (click)="onGoogleClick()" [disabled]="loading">
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.24v3.15C3.26 21.36 7.33 24 12 24z"/>
                <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.24C.45 8.15 0 9.99 0 12s.45 3.85 1.24 5.42l4.04-3.15z"/>
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.24 6.58l4.04 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
              </svg>
              <span>Sign in with Google</span>
            </button>
          </div>

          <div class="auth-divider">
            <span>OR SIGN IN WITH EMAIL</span>
          </div>

          <form (ngSubmit)="onSubmit()" class="auth-form">
            <!-- Email -->
            <div class="field-group">
              <label class="field-label">Email Address</label>
              <div class="input-wrapper">
                <span class="input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </span>
                <input
                  type="email"
                  class="field-input"
                  [(ngModel)]="email"
                  name="email"
                  placeholder="admin@shellmuze.com"
                  required
                  autocomplete="email"
                />
              </div>
            </div>

            <!-- Password -->
            <div class="field-group">
              <div class="label-row">
                <label class="field-label">Password</label>
              </div>
              <div class="input-wrapper">
                <span class="input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  class="field-input"
                  [(ngModel)]="password"
                  name="password"
                  placeholder="••••••••••"
                  required
                  autocomplete="current-password"
                />
                <button type="button" class="eye-toggle" (click)="showPassword = !showPassword">
                  <svg *ngIf="!showPassword" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg *ngIf="showPassword" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Submit -->
            <button type="submit" class="submit-btn" [disabled]="loading">
              <span class="spinner" *ngIf="loading"></span>
              <svg *ngIf="!loading" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                <polyline points="10 17 15 12 10 7"/>
                <line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
              {{ loading ? 'Signing in…' : 'Sign In to Admin Portal' }}
            </button>
          </form>

        </div>
      </div>

    </div>
  `,
  styles: [`
    .login-page {
      width: 100vw;
      height: 100vh;
      display: flex;
      overflow: hidden;
    }

    /* ── LEFT PANEL ── */
    .brand-panel {
      width: 42%;
      min-width: 380px;
      background: linear-gradient(145deg, #0D1525 0%, #080C14 60%, #0A1020 100%);
      border-right: 1px solid var(--border-color);
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .brand-panel-inner {
      position: relative;
      z-index: 2;
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 48px 48px 32px;
    }

    /* Decorative elements */
    .deco-line {
      position: absolute;
      border-radius: 2px;
    }

    .deco-line-1 {
      top: 0;
      left: 60px;
      width: 1px;
      height: 180px;
      background: linear-gradient(180deg, transparent, rgba(201, 169, 110, 0.25), transparent);
    }

    .deco-line-2 {
      bottom: 80px;
      right: 80px;
      width: 1px;
      height: 120px;
      background: linear-gradient(180deg, transparent, rgba(201, 169, 110, 0.15), transparent);
    }

    .deco-circle {
      position: absolute;
      width: 320px;
      height: 320px;
      border-radius: 50%;
      border: 1px solid rgba(201, 169, 110, 0.06);
      top: -100px;
      right: -120px;
      pointer-events: none;
    }

    .brand-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .brand-logo-mark {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      background: linear-gradient(135deg, #C9A96E, #B8924F);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 28px;
      box-shadow: 0 8px 24px rgba(201, 169, 110, 0.35);
    }

    .brand-title {
      font-family: var(--font-serif);
      font-size: 38px;
      font-weight: 700;
      color: var(--text-primary);
      letter-spacing: -0.02em;
      line-height: 1.1;
      margin-bottom: 10px;
    }

    .brand-tagline {
      font-size: 14px;
      color: var(--text-secondary);
      margin-bottom: 36px;
      line-height: 1.5;
    }

    .brand-divider {
      width: 40px;
      height: 2px;
      background: linear-gradient(90deg, var(--accent-gold), transparent);
      margin-bottom: 28px;
    }

    .brand-feature-list {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .brand-feature {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 13.5px;
      color: var(--text-secondary);
    }

    .feature-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--accent-gold);
      flex-shrink: 0;
      box-shadow: 0 0 8px rgba(201, 169, 110, 0.5);
    }

    .brand-panel-footer {
      font-size: 11px;
      color: var(--text-muted);
    }

    /* ── RIGHT PANEL ── */
    .form-panel {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 32px;
      background: var(--bg-main);
    }

    .form-card {
      width: 100%;
      max-width: 400px;
    }

    .form-header {
      margin-bottom: 32px;
    }

    .form-logo {
      margin-bottom: 24px;
    }

    .form-logo-sm {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      background: linear-gradient(135deg, #C9A96E, #B8924F);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 14px rgba(201, 169, 110, 0.3);
    }

    .form-title {
      font-size: 26px;
      font-weight: 800;
      color: var(--text-primary);
      letter-spacing: -0.02em;
      margin-bottom: 6px;
    }

    .form-subtitle {
      font-size: 13.5px;
      color: var(--text-muted);
    }

    /* Error */
    .error-alert {
      display: flex;
      align-items: center;
      gap: 10px;
      background: var(--danger-bg);
      color: var(--danger);
      border: 1px solid rgba(248, 113, 113, 0.2);
      padding: 11px 14px;
      border-radius: var(--radius-md);
      font-size: 13px;
      font-weight: 500;
      margin-bottom: 20px;
    }

    /* Form */
    .auth-form {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }

    .field-group {
      display: flex;
      flex-direction: column;
      gap: 7px;
    }

    .field-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--text-secondary);
    }

    .label-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .input-wrapper {
      position: relative;
    }

    .input-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      display: flex;
      align-items: center;
      pointer-events: none;
    }

    .field-input {
      width: 100%;
      background: var(--bg-input);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      padding: 12px 16px 12px 42px;
      color: var(--text-primary);
      font-family: var(--font-sans);
      font-size: 14px;
      outline: none;
      transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
    }

    .field-input::placeholder {
      color: var(--text-muted);
    }

    .field-input:focus {
      border-color: var(--accent-gold);
      box-shadow: 0 0 0 3px var(--accent-gold-glow);
    }

    .eye-toggle {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-muted);
      display: flex;
      align-items: center;
      padding: 0;
      transition: color var(--transition-fast);
    }

    .eye-toggle:hover {
      color: var(--text-secondary);
    }

    /* Submit button */
    .submit-btn {
      width: 100%;
      background: linear-gradient(135deg, #C9A96E 0%, #B8924F 100%);
      color: #0B0F17;
      font-family: var(--font-sans);
      font-size: 14px;
      font-weight: 700;
      padding: 14px;
      border-radius: var(--radius-md);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      letter-spacing: 0.02em;
      transition: all var(--transition-base);
      box-shadow: 0 4px 20px rgba(201, 169, 110, 0.3);
      margin-top: 6px;
    }

    .submit-btn:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 28px rgba(201, 169, 110, 0.45);
    }

    .submit-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    /* Demo notice */
    .demo-notice {
      margin-top: 28px;
      padding-top: 20px;
      border-top: 1px solid var(--border-color);
      text-align: center;
    }

    .demo-label {
      font-size: 10.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--text-muted);
      margin-bottom: 8px;
    }

    .demo-code {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: var(--accent-gold);
      background: var(--accent-gold-glow);
      border: 1px solid var(--border-highlight);
      padding: 7px 14px;
      border-radius: var(--radius-md);
      display: inline-block;
    }

    /* Google Auth */
    .google-auth-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      margin-bottom: 18px;
    }

    .google-render-btn {
      display: flex;
      justify-content: center;
      width: 100%;
      min-height: 40px;
    }

    .custom-google-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      background: #FFFFFF;
      color: #1F2937;
      font-size: 13.5px;
      font-weight: 600;
      padding: 10px 18px;
      border-radius: var(--radius-md, 10px);
      border: 1px solid #D1D5DB;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    }

    .custom-google-btn:hover:not(:disabled) {
      background: #F9FAFB;
      border-color: #9CA3AF;
      box-shadow: 0 3px 6px rgba(0,0,0,0.12);
    }

    .auth-divider {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 18px 0;
      color: var(--text-muted);
      font-size: 10.5px;
      letter-spacing: 0.1em;
      font-weight: 700;
    }

    .auth-divider::before,
    .auth-divider::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid var(--border-color);
    }

    .auth-divider span {
      padding: 0 12px;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .brand-panel {
        display: none;
      }
    }
  `]
})
export class LoginComponent implements AfterViewInit {
  email = '';
  password = '';
  loading = false;
  showPassword = false;
  errorMessage = '';

  constructor(private authService: AuthService, private router: Router) {}

  ngAfterViewInit(): void {
    this.initGoogleSignIn();
  }

  initGoogleSignIn(): void {
    if (typeof google !== 'undefined' && google.accounts?.id) {
      try {
        google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: any) => this.handleGoogleResponse(response)
        });

        const el = document.getElementById('admin-google-btn');
        if (el) {
          el.innerHTML = '';
          google.accounts.id.renderButton(el, {
            theme: 'outline',
            size: 'large',
            width: 320,
            text: 'continue_with',
            shape: 'rectangular',
            logo_alignment: 'left'
          });
        }
      } catch (err) {
        console.warn('Admin Google GSI init warning:', err);
      }
    } else {
      setTimeout(() => this.initGoogleSignIn(), 500);
    }
  }

  handleGoogleResponse(response: any): void {
    if (response?.credential) {
      this.loading = true;
      this.errorMessage = '';
      this.authService.loginWithGoogle(response.credential).subscribe({
        next: () => {
          this.loading = false;
          this.router.navigate(['/dashboard']);
        },
        error: (err) => {
          this.loading = false;
          this.errorMessage = err.error?.message || 'Google authentication failed or unauthorized admin account.';
        }
      });
    }
  }

  onGoogleClick(): void {
    if (typeof google !== 'undefined' && google.accounts?.id) {
      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          this.initiateGoogleOAuthFallback();
        }
      });
    } else {
      this.initiateGoogleOAuthFallback();
    }
  }

  initiateGoogleOAuthFallback(): void {
    const redirectUri = window.location.origin + '/login';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${environment.googleClientId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=token%20id_token&scope=openid%20email%20profile&nonce=${Date.now()}`;
    window.open(authUrl, 'GoogleAuthPopup', 'width=500,height=600,top=100,left=100');
  }

  onSubmit() {
    if (!this.email || !this.password) {
      this.errorMessage = 'Please enter both email and password';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authService.login({ email: this.email, password: this.password }).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err.error?.message || 'Login failed. Please verify credentials.';
      }
    });
  }
}
